import { FormEvent, useEffect, useState } from "react";
import { Download, RefreshCw, Search } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminUser,
  PaginationMeta,
  ProductTrialReservation,
  ReservationStatus,
  exportProductTrialReservations,
  getCurrentAdmin,
  listProductTrialReservations,
  logout,
  updateProductTrialReservationStatus,
} from "./api";
import Pagination from "./components/Pagination";

const statusLabels: Record<ReservationStatus, string> = {
  New: "新預約",
  Contacted: "已聯絡",
  Booked: "已預約",
  Closed: "已結案",
};

const statusClass: Record<ReservationStatus, string> = {
  New: "bg-[#e9f4ec] text-[#245438]",
  Contacted: "bg-[#eef3f8] text-[#255071]",
  Booked: "bg-[#fff6df] text-[#7a5610]",
  Closed: "bg-[#eeeeee] text-[#59635d]",
};

const statusBorderClass: Record<ReservationStatus, string> = {
  New: "border-l-[#4f8b61]",
  Contacted: "border-l-[#4e7fa3]",
  Booked: "border-l-[#d3a33f]",
  Closed: "border-l-[#9ba59f]",
};

const statusAccentClass: Record<ReservationStatus, string> = {
  New: "text-[#245438]",
  Contacted: "text-[#255071]",
  Booked: "text-[#7a5610]",
  Closed: "text-[#59635d]",
};

const emptyCounts: Record<ReservationStatus, number> = {
  New: 0,
  Contacted: 0,
  Booked: 0,
  Closed: 0,
};

const defaultPagination: PaginationMeta = {
  Page: 1,
  PerPage: 20,
  Total: 0,
  TotalPages: 1,
};

function countByStatus(rows: ProductTrialReservation[]) {
  return rows.reduce<Record<ReservationStatus, number>>((acc, item) => {
    acc[item.Status] += 1;
    return acc;
  }, { ...emptyCounts });
}

export default function AdminProductTrialReservations() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get("status") || "";
  const [user, setUser] = useState<AdminUser | null>(null);
  const [reservations, setReservations] = useState<ProductTrialReservation[]>([]);
  const [counts, setCounts] = useState<Record<ReservationStatus, number>>(emptyCounts);
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [status, setStatus] = useState(initialStatus);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState("");

  async function load(filters = { Status: status, Keyword: keyword, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setUser(me.User);
      const listData = await listProductTrialReservations(filters);
      setReservations(listData.Reservations);
      setCounts(listData.Counts || countByStatus(listData.Reservations));
      setPagination(listData.Pagination || defaultPagination);
    } catch (error) {
      if (error instanceof Error) setMessage(error.message);
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: initialStatus, Keyword: "", Page: 1, PerPage: pagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  async function handleStatusChange(item: ProductTrialReservation, nextStatus: ReservationStatus) {
    const previous = reservations;
    setReservations((current) => current.map((row) => (row.ReservationId === item.ReservationId ? { ...row, Status: nextStatus } : row)));
    try {
      await updateProductTrialReservationStatus(item.ReservationId, nextStatus);
      await load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setReservations(previous);
      setMessage(error instanceof Error ? error.message : "狀態更新失敗，請稍後再試。");
    }
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
  }

  async function handleExport() {
    setExporting(true);
    setMessage("");
    try {
      await exportProductTrialReservations({ Status: status, Keyword: keyword.trim() });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "匯出失敗，請稍後再試。");
    } finally {
      setExporting(false);
    }
  }

  return (
    <AdminLayout title="預約試戴列表" user={user} onLogout={handleLogout}>
      <div className="mb-4 md:hidden">
        <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button className={`flex min-w-[96px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${status === "" ? "border-[#315746] bg-[#315746] text-white" : "border-[#dfe6e1] bg-white text-[#315746]"}`} onClick={() => { setStatus(""); load({ Status: "", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <span className="text-xs font-bold">全部</span>
            <span className="ml-3 text-lg font-bold">{Object.values(counts).reduce((sum, value) => sum + value, 0)}</span>
          </button>

          <button className={`flex min-w-[108px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${status === "Pending" ? "border-[#315746] bg-[#315746] text-white" : "border-[#dfe6e1] bg-white"}`} onClick={() => { setStatus("Pending"); load({ Status: "Pending", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage }); }} type="button">
            <span className={`text-xs font-bold ${status === "Pending" ? "text-white" : "text-[#8a6a2f]"}`}>待處理</span>
            <span className="ml-3 text-lg font-bold">{counts.New + counts.Contacted}</span>
          </button>

          {(["New", "Contacted", "Booked", "Closed"] as ReservationStatus[]).map((value) => (
            <button className={`flex min-w-[108px] shrink-0 items-center justify-between rounded-md border px-3 py-2 text-left transition ${status === value ? "border-[#315746] bg-[#315746] text-white" : "border-[#dfe6e1] bg-white"}`} key={value} onClick={() => { setStatus(value); load({ Status: value, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage }); }} type="button">
              <span className={`text-xs font-bold ${status === value ? "text-white" : statusAccentClass[value]}`}>{statusLabels[value]}</span>
              <span className="ml-3 text-lg font-bold">{counts[value] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-5 hidden gap-3 md:grid md:grid-cols-5">
        <button
          className="rounded-md border border-[#dfe6e1] border-l-4 border-l-[#c99a3e] bg-white p-4 text-left transition hover:border-[#b8c8bf]"
          onClick={() => {
            setStatus("Pending");
            load({ Status: "Pending", Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
          }}
          type="button"
        >
          <div className="mb-3 inline-flex rounded-full bg-[#f3ecdf] px-3 py-1 text-xs font-bold text-[#8a6a2f]">待處理合計</div>
          <div className="text-3xl font-bold text-[#203d32]">{counts.New + counts.Contacted}</div>
        </button>
        {(["New", "Contacted", "Booked", "Closed"] as ReservationStatus[]).map((value) => (
          <button
            className={`rounded-md border border-[#dfe6e1] border-l-4 bg-white p-4 text-left transition hover:border-[#b8c8bf] ${statusBorderClass[value]}`}
            key={value}
            onClick={() => {
              setStatus(value);
              load({ Status: value, Keyword: keyword.trim(), Page: 1, PerPage: pagination.PerPage });
            }}
            type="button"
          >
            <div className={`mb-3 inline-flex rounded-full px-3 py-1 text-xs font-bold ${statusClass[value]}`}>
              {statusLabels[value]}
            </div>
            <div className="text-3xl font-bold text-[#203d32]">{counts[value] || 0}</div>
          </button>
        ))}
      </div>

      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 md:flex md:flex-wrap md:items-end md:p-4" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-1 md:min-w-[120px]">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="trial-status">狀態</label>
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="trial-status" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Pending">待處理（新預約＋已聯絡）</option>
            {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </div>
        <div className="grid min-w-0 gap-1 md:min-w-[280px] md:flex-1">
          <label className="text-sm font-bold text-[#59665f]" htmlFor="trial-keyword">關鍵字</label>
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" id="trial-keyword" placeholder="姓名、電話、門市、商品名稱、商品編號" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </div>
        <button className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] md:w-auto">
          <Search size={18} />
          查詢
        </button>
        <div className="grid grid-cols-2 gap-2 md:flex md:gap-3">
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] hover:bg-[#f0f5f2] md:px-5" onClick={() => load({ Status: status, Keyword: keyword.trim(), Page: pagination.Page, PerPage: pagination.PerPage })} type="button">
            <RefreshCw size={18} />
            重新整理
          </button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#315746] bg-white px-3 font-bold text-[#315746] hover:bg-[#f0f5f2] disabled:opacity-60 md:px-5" disabled={exporting} onClick={handleExport} type="button">
            <Download size={18} />
            {exporting ? "匯出中" : "匯出名單"}
          </button>
        </div>
      </form>

      {message && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{message}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="max-h-[62vh] overflow-auto">
          <table className="w-full min-w-[1120px] border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="px-4 py-3">建立時間</th>
                <th className="px-4 py-3">預約者</th>
                <th className="px-4 py-3">試戴商品</th>
                <th className="px-4 py-3">門市</th>
                <th className="px-4 py-3">預約時段</th>
                <th className="px-4 py-3">備註</th>
                <th className="px-4 py-3">狀態</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>載入中...</td></tr>
              ) : reservations.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>目前沒有符合條件的試戴預約。</td></tr>
              ) : reservations.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.ReservationId}>
                  <td className="px-4 py-4 align-top text-sm">{item.CreatedAt}</td>
                  <td className="px-4 py-4 align-top">
                    <strong>{item.Name}</strong>
                    <div className="text-sm text-[#59665f]">{item.Phone}</div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <strong className="text-[#203d32]">{item.ProductName}</strong>
                    {item.ProductCode && <div className="text-xs text-[#7b877f]">{item.ProductCode}</div>}
                  </td>
                  <td className="px-4 py-4 align-top">{item.Store}</td>
                  <td className="px-4 py-4 align-top">
                    <div>{item.AppointmentDate}</div>
                    <div className="text-sm text-[#59665f]">{item.AppointmentTime}</div>
                  </td>
                  <td className="max-w-[340px] px-4 py-4 align-top text-sm text-[#59665f]">{item.Note || "-"}</td>
                  <td className="px-4 py-4 align-top">
                    <select className={`h-10 rounded-md border border-[#cfd8d2] px-3 font-bold outline-none ${statusClass[item.Status]}`} value={item.Status} onChange={(event) => handleStatusChange(item, event.target.value as ReservationStatus)}>
                      {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Keyword: keyword.trim(), Page: page, PerPage: pagination.PerPage })} />
      </div>
    </AdminLayout>
  );
}
